import React, { useEffect, useState, Component } from 'react';
import { ShieldAlertIcon, AlertCircleIcon, AlertTriangleIcon, InfoIcon, FilterIcon, RefreshCwIcon, CheckCircleIcon, XCircleIcon, SearchIcon, ClockIcon, UserIcon, GlobeIcon, TargetIcon } from 'lucide-react';
// Security event types - this should match your backend definitions
export enum SecuritySeverity {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  CRITICAL = 'critical',
}
export enum SecurityCategory {
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  DATA_ACCESS = 'data_access',
  DATA_MODIFICATION = 'data_modification',
  ADMIN = 'admin',
  PAYMENT = 'payment',
  SYSTEM = 'system',
}
// Sample security event interface
interface SecurityEvent {
  id: string;
  timestamp: Date;
  category: SecurityCategory;
  severity: SecuritySeverity;
  eventType: string;
  userId?: string;
  targetId?: string;
  targetType?: string;
  ipAddress?: string;
  location?: string;
  details: Record<string, any>;
  acknowledged?: boolean;
  acknowledgedBy?: string;
  acknowledgedAt?: Date;
}
// Sample filter state
interface FilterState {
  severity: SecuritySeverity[];
  category: SecurityCategory[];
  timeRange: 'last24h' | 'last7d' | 'last30d' | 'custom';
  startDate?: Date;
  endDate?: Date;
  userId?: string;
  ipAddress?: string;
  acknowledged?: boolean | null; // null means both
  searchTerm?: string;
}
// Mock function to fetch security events
// In a real implementation, this would call your API
const fetchSecurityEvents = async (filters: FilterState): Promise<SecurityEvent[]> => {
  // This is a mock implementation
  // In a real app, you would call your API with the filters
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));
  // Mock data
  const mockEvents: SecurityEvent[] = [{
    id: 'sec_1',
    timestamp: new Date(Date.now() - 30 * 60 * 1000),
    category: SecurityCategory.AUTHENTICATION,
    severity: SecuritySeverity.WARNING,
    eventType: 'auth.login.failed',
    userId: 'user123',
    ipAddress: '192.168.1.1',
    location: 'New York, USA',
    details: {
      reason: 'invalid_password',
      attemptCount: 3
    },
    acknowledged: false
  }, {
    id: 'sec_2',
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
    category: SecurityCategory.ADMIN,
    severity: SecuritySeverity.CRITICAL,
    eventType: 'admin.user.role.change',
    userId: 'admin456',
    targetId: 'user789',
    targetType: 'user',
    ipAddress: '192.168.1.2',
    location: 'San Francisco, USA',
    details: {
      oldRoles: ['user'],
      newRoles: ['user', 'admin'],
      reason: 'Requested by management'
    },
    acknowledged: true,
    acknowledgedBy: 'admin999',
    acknowledgedAt: new Date(Date.now() - 1 * 60 * 60 * 1000) // 1 hour ago
  }, {
    id: 'sec_3',
    timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000),
    category: SecurityCategory.PAYMENT,
    severity: SecuritySeverity.ERROR,
    eventType: 'payment.unusual.amount',
    userId: 'seller123',
    targetId: 'payment456',
    targetType: 'payment',
    details: {
      amount: 9999.99,
      currency: 'USD',
      averageAmount: 100.0,
      deviationPercent: 9900
    },
    acknowledged: false
  }, {
    id: 'sec_4',
    timestamp: new Date(Date.now() - 5 * 60 * 1000),
    category: SecurityCategory.SYSTEM,
    severity: SecuritySeverity.INFO,
    eventType: 'system.config.changed',
    userId: 'admin456',
    details: {
      setting: 'security.login.max_attempts',
      oldValue: 5,
      newValue: 3
    },
    acknowledged: false
  }, {
    id: 'sec_5',
    timestamp: new Date(Date.now() - 25 * 60 * 60 * 1000),
    category: SecurityCategory.DATA_ACCESS,
    severity: SecuritySeverity.WARNING,
    eventType: 'data.sensitive.access',
    userId: 'user123',
    targetId: 'payment789',
    targetType: 'payment',
    ipAddress: '192.168.1.3',
    location: 'Chicago, USA',
    details: {
      dataType: 'payment_details',
      reason: 'customer_support'
    },
    acknowledged: true,
    acknowledgedBy: 'admin456',
    acknowledgedAt: new Date(Date.now() - 24 * 60 * 60 * 1000) // 24 hours ago
  }];
  // Apply filters
  let filteredEvents = [...mockEvents];
  // Filter by time range
  const now = new Date();
  let startDate: Date;
  switch (filters.timeRange) {
    case 'last24h':
      startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      break;
    case 'last7d':
      startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      break;
    case 'last30d':
      startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      break;
    case 'custom':
      startDate = filters.startDate || new Date(0);
      break;
  }
  const endDate = filters.endDate || now;
  filteredEvents = filteredEvents.filter(event => event.timestamp >= startDate && event.timestamp <= endDate);
  // Filter by severity
  if (filters.severity.length > 0) {
    filteredEvents = filteredEvents.filter(event => filters.severity.includes(event.severity));
  }
  // Filter by category
  if (filters.category.length > 0) {
    filteredEvents = filteredEvents.filter(event => filters.category.includes(event.category));
  }
  // Filter by user ID
  if (filters.userId) {
    filteredEvents = filteredEvents.filter(event => event.userId?.includes(filters.userId));
  }
  // Filter by IP address
  if (filters.ipAddress) {
    filteredEvents = filteredEvents.filter(event => event.ipAddress?.includes(filters.ipAddress));
  }
  // Filter by acknowledgement status
  if (filters.acknowledged !== null) {
    filteredEvents = filteredEvents.filter(event => event.acknowledged === filters.acknowledged);
  }
  // Filter by search term (across multiple fields)
  if (filters.searchTerm) {
    const searchLower = filters.searchTerm.toLowerCase();
    filteredEvents = filteredEvents.filter(event => event.eventType.toLowerCase().includes(searchLower) || event.userId?.toLowerCase().includes(searchLower) || event.targetId?.toLowerCase().includes(searchLower) || event.ipAddress?.toLowerCase().includes(searchLower) || event.location?.toLowerCase().includes(searchLower) || JSON.stringify(event.details).toLowerCase().includes(searchLower));
  }
  return filteredEvents;
};
// Mock function to acknowledge a security event
const acknowledgeSecurityEvent = async (eventId: string, notes?: string): Promise<boolean> => {
  // This is a mock implementation
  // In a real app, you would call your API
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));
  // Always return success in this mock
  return true;
};
// Security Dashboard Component
export const SecurityEventsDashboard: React.FC = () => {
  // State for events and loading
  const [events, setEvents] = useState<SecurityEvent[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  // State for selected event details
  const [selectedEvent, setSelectedEvent] = useState<SecurityEvent | null>(null);
  // State for acknowledgement
  const [acknowledging, setAcknowledging] = useState<boolean>(false);
  const [acknowledgeNotes, setAcknowledgeNotes] = useState<string>('');
  // State for filters
  const [filters, setFilters] = useState<FilterState>({
    severity: [],
    category: [],
    timeRange: 'last24h',
    acknowledged: null
  });
  // State for filter panel visibility
  const [showFilters, setShowFilters] = useState<boolean>(false);
  // Load events on mount and when filters change
  useEffect(() => {
    const loadEvents = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchSecurityEvents(filters);
        setEvents(data);
      } catch (err) {
        setError('Failed to load security events. Please try again.');
        console.error('Error loading security events:', err);
      } finally {
        setLoading(false);
      }
    };
    loadEvents();
  }, [filters]);
  // Handle filter changes
  const handleFilterChange = (changes: Partial<FilterState>) => {
    setFilters(prev => ({
      ...prev,
      ...changes
    }));
  };
  // Handle severity filter toggle
  const toggleSeverityFilter = (severity: SecuritySeverity) => {
    setFilters(prev => {
      if (prev.severity.includes(severity)) {
        return {
          ...prev,
          severity: prev.severity.filter(s => s !== severity)
        };
      } else {
        return {
          ...prev,
          severity: [...prev.severity, severity]
        };
      }
    });
  };
  // Handle category filter toggle
  const toggleCategoryFilter = (category: SecurityCategory) => {
    setFilters(prev => {
      if (prev.category.includes(category)) {
        return {
          ...prev,
          category: prev.category.filter(c => c !== category)
        };
      } else {
        return {
          ...prev,
          category: [...prev.category, category]
        };
      }
    });
  };
  // Handle event selection
  const handleSelectEvent = (event: SecurityEvent) => {
    setSelectedEvent(event);
  };
  // Handle event acknowledgement
  const handleAcknowledge = async () => {
    if (!selectedEvent) return;
    setAcknowledging(true);
    try {
      const success = await acknowledgeSecurityEvent(selectedEvent.id, acknowledgeNotes);
      if (success) {
        // Update the local state
        setEvents(prev => prev.map(event => event.id === selectedEvent.id ? {
          ...event,
          acknowledged: true,
          acknowledgedBy: 'current-user',
          acknowledgedAt: new Date()
        } : event));
        // Update the selected event
        setSelectedEvent(prev => prev ? {
          ...prev,
          acknowledged: true,
          acknowledgedBy: 'current-user',
          acknowledgedAt: new Date()
        } : null);
        // Reset notes
        setAcknowledgeNotes('');
      }
    } catch (err) {
      console.error('Error acknowledging event:', err);
      // Handle error (show message, etc.)
    } finally {
      setAcknowledging(false);
    }
  };
  // Handle refresh
  const handleRefresh = () => {
    // Re-fetch events with current filters
    const loadEvents = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchSecurityEvents(filters);
        setEvents(data);
      } catch (err) {
        setError('Failed to load security events. Please try again.');
        console.error('Error loading security events:', err);
      } finally {
        setLoading(false);
      }
    };
    loadEvents();
  };
  // Render severity badge
  const renderSeverityBadge = (severity: SecuritySeverity) => {
    let icon;
    let colorClass;
    switch (severity) {
      case SecuritySeverity.CRITICAL:
        icon = <AlertCircleIcon className="w-4 h-4" data-id="element-2428" />;
        colorClass = 'bg-red-600 text-white';
        break;
      case SecuritySeverity.ERROR:
        icon = <AlertCircleIcon className="w-4 h-4" data-id="element-2429" />;
        colorClass = 'bg-red-500 text-white';
        break;
      case SecuritySeverity.WARNING:
        icon = <AlertTriangleIcon className="w-4 h-4" data-id="element-2430" />;
        colorClass = 'bg-yellow-500 text-white';
        break;
      case SecuritySeverity.INFO:
      default:
        icon = <InfoIcon className="w-4 h-4" data-id="element-2431" />;
        colorClass = 'bg-blue-500 text-white';
        break;
    }
    return <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colorClass}`} data-id="element-2432">
        {icon}
        <span className="ml-1" data-id="element-2433">{severity.toUpperCase()}</span>
      </span>;
  };
  // Render category badge
  const renderCategoryBadge = (category: SecurityCategory) => {
    let colorClass;
    switch (category) {
      case SecurityCategory.AUTHENTICATION:
        colorClass = 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
        break;
      case SecurityCategory.AUTHORIZATION:
        colorClass = 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300';
        break;
      case SecurityCategory.DATA_ACCESS:
        colorClass = 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
        break;
      case SecurityCategory.DATA_MODIFICATION:
        colorClass = 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
        break;
      case SecurityCategory.ADMIN:
        colorClass = 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300';
        break;
      case SecurityCategory.PAYMENT:
        colorClass = 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300';
        break;
      case SecurityCategory.SYSTEM:
      default:
        colorClass = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
        break;
    }
    return <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${colorClass}`} data-id="element-2434">
        {category.replace('_', ' ')}
      </span>;
  };
  // Format date for display
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(date);
  };
  // Format relative time
  const formatRelativeTime = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    if (diffSec < 60) {
      return `${diffSec} second${diffSec !== 1 ? 's' : ''} ago`;
    } else if (diffMin < 60) {
      return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
    } else if (diffHour < 24) {
      return `${diffHour} hour${diffHour !== 1 ? 's' : ''} ago`;
    } else {
      return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;
    }
  };
  return <div className="bg-white dark:bg-[#0F1F1D] min-h-screen" data-id="element-2435">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-2436">
        <div className="flex justify-between items-center mb-6" data-id="element-2437">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center" data-id="element-2438">
            <ShieldAlertIcon className="w-6 h-6 mr-2 text-[#2DB489] dark:text-[#4ED1A0]" data-id="element-2439" />
            Security Events Dashboard
          </h1>
          <div className="flex space-x-2" data-id="element-2440">
            <button onClick={() => setShowFilters(!showFilters)} className="px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm bg-white dark:bg-[#1A3330] text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-[#264D49] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] flex items-center" data-id="element-2441">
              <FilterIcon className="w-4 h-4 mr-1" data-id="element-2442" />
              Filters
            </button>
            <button onClick={handleRefresh} className="px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm bg-white dark:bg-[#1A3330] text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-[#264D49] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] flex items-center" disabled={loading} data-id="element-2443">
              <RefreshCwIcon className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} data-id="element-2444" />
              Refresh
            </button>
          </div>
        </div>
        {/* Filters Panel */}
        {showFilters && <div className="bg-gray-50 dark:bg-[#162825] rounded-lg p-4 mb-6 shadow-sm" data-id="element-2445">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-2446">
              Filters
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2447">
              {/* Severity Filters */}
              <div data-id="element-2448">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2449">
                  Severity
                </h3>
                <div className="space-y-2" data-id="element-2450">
                  {Object.values(SecuritySeverity).map(severity => <label key={severity} className="flex items-center" data-id="element-2451">
                      <input type="checkbox" checked={filters.severity.includes(severity)} onChange={() => toggleSeverityFilter(severity)} className="h-4 w-4 text-[#2DB489] dark:text-[#4ED1A0] focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] border-gray-300 dark:border-[#1A3330] rounded" data-id="element-2452" />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300" data-id="element-2453">
                        {severity.toUpperCase()}
                      </span>
                    </label>)}
                </div>
              </div>
              {/* Category Filters */}
              <div data-id="element-2454">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2455">
                  Category
                </h3>
                <div className="space-y-2" data-id="element-2456">
                  {Object.values(SecurityCategory).map(category => <label key={category} className="flex items-center" data-id="element-2457">
                      <input type="checkbox" checked={filters.category.includes(category)} onChange={() => toggleCategoryFilter(category)} className="h-4 w-4 text-[#2DB489] dark:text-[#4ED1A0] focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] border-gray-300 dark:border-[#1A3330] rounded" data-id="element-2458" />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300" data-id="element-2459">
                        {category.replace('_', ' ')}
                      </span>
                    </label>)}
                </div>
              </div>
              {/* Time Range Filters */}
              <div data-id="element-2460">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2461">
                  Time Range
                </h3>
                <div className="space-y-2" data-id="element-2462">
                  <label className="flex items-center" data-id="element-2463">
                    <input type="radio" checked={filters.timeRange === 'last24h'} onChange={() => handleFilterChange({
                  timeRange: 'last24h'
                })} className="h-4 w-4 text-[#2DB489] dark:text-[#4ED1A0] focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] border-gray-300 dark:border-[#1A3330]" data-id="element-2464" />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300" data-id="element-2465">
                      Last 24 hours
                    </span>
                  </label>
                  <label className="flex items-center" data-id="element-2466">
                    <input type="radio" checked={filters.timeRange === 'last7d'} onChange={() => handleFilterChange({
                  timeRange: 'last7d'
                })} className="h-4 w-4 text-[#2DB489] dark:text-[#4ED1A0] focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] border-gray-300 dark:border-[#1A3330]" data-id="element-2467" />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300" data-id="element-2468">
                      Last 7 days
                    </span>
                  </label>
                  <label className="flex items-center" data-id="element-2469">
                    <input type="radio" checked={filters.timeRange === 'last30d'} onChange={() => handleFilterChange({
                  timeRange: 'last30d'
                })} className="h-4 w-4 text-[#2DB489] dark:text-[#4ED1A0] focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] border-gray-300 dark:border-[#1A3330]" data-id="element-2470" />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300" data-id="element-2471">
                      Last 30 days
                    </span>
                  </label>
                  <label className="flex items-center" data-id="element-2472">
                    <input type="radio" checked={filters.timeRange === 'custom'} onChange={() => handleFilterChange({
                  timeRange: 'custom'
                })} className="h-4 w-4 text-[#2DB489] dark:text-[#4ED1A0] focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] border-gray-300 dark:border-[#1A3330]" data-id="element-2473" />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300" data-id="element-2474">
                      Custom range
                    </span>
                  </label>
                  {filters.timeRange === 'custom' && <div className="mt-2 space-y-2" data-id="element-2475">
                      <div data-id="element-2476">
                        <label className="block text-xs text-gray-700 dark:text-gray-300" data-id="element-2477">
                          Start Date
                        </label>
                        <input type="datetime-local" value={filters.startDate?.toISOString().slice(0, 16) || ''} onChange={e => {
                    const date = e.target.value ? new Date(e.target.value) : undefined;
                    handleFilterChange({
                      startDate: date
                    });
                  }} className="mt-1 block w-full rounded-md border-gray-300 dark:border-[#1A3330] shadow-sm focus:border-[#2DB489] dark:focus:border-[#4ED1A0] focus:ring focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:ring-opacity-50 dark:bg-[#1A3330] dark:text-white text-sm" data-id="element-2478" />
                      </div>
                      <div data-id="element-2479">
                        <label className="block text-xs text-gray-700 dark:text-gray-300" data-id="element-2480">
                          End Date
                        </label>
                        <input type="datetime-local" value={filters.endDate?.toISOString().slice(0, 16) || ''} onChange={e => {
                    const date = e.target.value ? new Date(e.target.value) : undefined;
                    handleFilterChange({
                      endDate: date
                    });
                  }} className="mt-1 block w-full rounded-md border-gray-300 dark:border-[#1A3330] shadow-sm focus:border-[#2DB489] dark:focus:border-[#4ED1A0] focus:ring focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:ring-opacity-50 dark:bg-[#1A3330] dark:text-white text-sm" data-id="element-2481" />
                      </div>
                    </div>}
                </div>
              </div>
              {/* Other Filters */}
              <div data-id="element-2482">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2483">
                  Additional Filters
                </h3>
                <div className="space-y-4" data-id="element-2484">
                  {/* User ID filter */}
                  <div data-id="element-2485">
                    <label className="block text-xs text-gray-700 dark:text-gray-300" data-id="element-2486">
                      User ID
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm" data-id="element-2487">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2488">
                        <UserIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" data-id="element-2489" />
                      </div>
                      <input type="text" value={filters.userId || ''} onChange={e => handleFilterChange({
                    userId: e.target.value
                  })} placeholder="Filter by user ID" className="block w-full pl-10 pr-3 py-2 rounded-md border-gray-300 dark:border-[#1A3330] shadow-sm focus:border-[#2DB489] dark:focus:border-[#4ED1A0] focus:ring focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:ring-opacity-50 dark:bg-[#1A3330] dark:text-white text-sm" data-id="element-2490" />
                    </div>
                  </div>
                  {/* IP Address filter */}
                  <div data-id="element-2491">
                    <label className="block text-xs text-gray-700 dark:text-gray-300" data-id="element-2492">
                      IP Address
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm" data-id="element-2493">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2494">
                        <GlobeIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" data-id="element-2495" />
                      </div>
                      <input type="text" value={filters.ipAddress || ''} onChange={e => handleFilterChange({
                    ipAddress: e.target.value
                  })} placeholder="Filter by IP address" className="block w-full pl-10 pr-3 py-2 rounded-md border-gray-300 dark:border-[#1A3330] shadow-sm focus:border-[#2DB489] dark:focus:border-[#4ED1A0] focus:ring focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:ring-opacity-50 dark:bg-[#1A3330] dark:text-white text-sm" data-id="element-2496" />
                    </div>
                  </div>
                  {/* Acknowledgement status filter */}
                  <div data-id="element-2497">
                    <label className="block text-xs text-gray-700 dark:text-gray-300" data-id="element-2498">
                      Status
                    </label>
                    <select value={filters.acknowledged === null ? 'all' : filters.acknowledged ? 'acknowledged' : 'unacknowledged'} onChange={e => {
                  const value = e.target.value;
                  let acknowledged = null;
                  if (value === 'acknowledged') acknowledged = true;
                  if (value === 'unacknowledged') acknowledged = false;
                  handleFilterChange({
                    acknowledged
                  });
                }} className="mt-1 block w-full rounded-md border-gray-300 dark:border-[#1A3330] shadow-sm focus:border-[#2DB489] dark:focus:border-[#4ED1A0] focus:ring focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:ring-opacity-50 dark:bg-[#1A3330] dark:text-white text-sm" data-id="element-2499">
                      <option value="all" data-id="element-2500">All Events</option>
                      <option value="acknowledged" data-id="element-2501">Acknowledged</option>
                      <option value="unacknowledged" data-id="element-2502">Unacknowledged</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            {/* Search bar */}
            <div className="mt-4" data-id="element-2503">
              <div className="relative rounded-md shadow-sm" data-id="element-2504">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2505">
                  <SearchIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" data-id="element-2506" />
                </div>
                <input type="text" value={filters.searchTerm || ''} onChange={e => handleFilterChange({
              searchTerm: e.target.value
            })} placeholder="Search across all fields..." className="block w-full pl-10 pr-3 py-2 rounded-md border-gray-300 dark:border-[#1A3330] shadow-sm focus:border-[#2DB489] dark:focus:border-[#4ED1A0] focus:ring focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:ring-opacity-50 dark:bg-[#1A3330] dark:text-white" data-id="element-2507" />
              </div>
            </div>
            {/* Filter actions */}
            <div className="mt-4 flex justify-end space-x-2" data-id="element-2508">
              <button onClick={() => setFilters({
            severity: [],
            category: [],
            timeRange: 'last24h',
            acknowledged: null
          })} className="px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm bg-white dark:bg-[#1A3330] text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-[#264D49] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0]" data-id="element-2509">
                Reset Filters
              </button>
            </div>
          </div>}
        {/* Main content area */}
        <div className="flex flex-col lg:flex-row gap-6" data-id="element-2510">
          {/* Events list */}
          <div className="w-full lg:w-1/2 xl:w-3/5" data-id="element-2511">
            <div className="bg-white dark:bg-[#162825] shadow-sm rounded-lg overflow-hidden" data-id="element-2512">
              <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-[#1A3330]" data-id="element-2513">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white" data-id="element-2514">
                  Security Events
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400" data-id="element-2515">
                  {events.length} events found
                </p>
              </div>
              {loading ? <div className="flex justify-center items-center py-12" data-id="element-2516">
                  <RefreshCwIcon className="w-8 h-8 text-[#2DB489] dark:text-[#4ED1A0] animate-spin" data-id="element-2517" />
                  <span className="ml-2 text-gray-700 dark:text-gray-300" data-id="element-2518">
                    Loading events...
                  </span>
                </div> : error ? <div className="px-4 py-5 sm:p-6 text-center" data-id="element-2519">
                  <XCircleIcon className="w-8 h-8 text-red-500 mx-auto" data-id="element-2520" />
                  <p className="mt-2 text-sm text-gray-700 dark:text-gray-300" data-id="element-2521">
                    {error}
                  </p>
                  <button onClick={handleRefresh} className="mt-3 inline-flex items-center px-3 py-2 border border-gray-300 dark:border-[#1A3330] shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-[#1A3330] hover:bg-gray-50 dark:hover:bg-[#264D49] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0]" data-id="element-2522">
                    <RefreshCwIcon className="h-4 w-4 mr-1" data-id="element-2523" />
                    Retry
                  </button>
                </div> : events.length === 0 ? <div className="px-4 py-5 sm:p-6 text-center" data-id="element-2524">
                  <InfoIcon className="w-8 h-8 text-gray-400 mx-auto" data-id="element-2525" />
                  <p className="mt-2 text-sm text-gray-700 dark:text-gray-300" data-id="element-2526">
                    No security events found with the current filters.
                  </p>
                  <button onClick={() => setFilters({
                severity: [],
                category: [],
                timeRange: 'last24h',
                acknowledged: null
              })} className="mt-3 inline-flex items-center px-3 py-2 border border-gray-300 dark:border-[#1A3330] shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-[#1A3330] hover:bg-gray-50 dark:hover:bg-[#264D49] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0]" data-id="element-2527">
                    Reset Filters
                  </button>
                </div> : <ul className="divide-y divide-gray-200 dark:divide-[#1A3330] max-h-[calc(100vh-300px)] overflow-y-auto" data-id="element-2528">
                  {events.map(event => <li key={event.id} className={`px-4 py-4 sm:px-6 hover:bg-gray-50 dark:hover:bg-[#1A3330] cursor-pointer transition-colors ${selectedEvent?.id === event.id ? 'bg-gray-50 dark:bg-[#1A3330]' : ''}`} onClick={() => handleSelectEvent(event)} data-id="element-2529">
                      <div className="flex items-center justify-between" data-id="element-2530">
                        <div className="flex items-center" data-id="element-2531">
                          {renderSeverityBadge(event.severity)}
                          <span className="ml-2 text-sm font-medium text-gray-900 dark:text-white truncate max-w-xs" data-id="element-2532">
                            {event.eventType}
                          </span>
                        </div>
                        <div className="flex items-center" data-id="element-2533">
                          {event.acknowledged ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300" data-id="element-2534">
                              <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-2535" />
                              Acknowledged
                            </span> : <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300" data-id="element-2536">
                              <AlertTriangleIcon className="w-3 h-3 mr-1" data-id="element-2537" />
                              Unacknowledged
                            </span>}
                        </div>
                      </div>
                      <div className="mt-2 sm:flex sm:justify-between" data-id="element-2538">
                        <div className="sm:flex" data-id="element-2539">
                          <p className="flex items-center text-sm text-gray-500 dark:text-gray-400" data-id="element-2540">
                            {renderCategoryBadge(event.category)}
                            {event.userId && <span className="ml-2 flex items-center" data-id="element-2541">
                                <UserIcon className="flex-shrink-0 mr-1 h-4 w-4 text-gray-400 dark:text-gray-500" data-id="element-2542" />
                                {event.userId}
                              </span>}
                          </p>
                        </div>
                        <div className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400 sm:mt-0" data-id="element-2543">
                          <ClockIcon className="flex-shrink-0 mr-1 h-4 w-4 text-gray-400 dark:text-gray-500" data-id="element-2544" />
                          <p data-id="element-2545">{formatRelativeTime(event.timestamp)}</p>
                        </div>
                      </div>
                    </li>)}
                </ul>}
            </div>
          </div>
          {/* Event details */}
          <div className="w-full lg:w-1/2 xl:w-2/5" data-id="element-2546">
            <div className="bg-white dark:bg-[#162825] shadow-sm rounded-lg overflow-hidden h-full" data-id="element-2547">
              <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-[#1A3330]" data-id="element-2548">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white" data-id="element-2549">
                  Event Details
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400" data-id="element-2550">
                  {selectedEvent ? 'Detailed information about the selected event' : 'Select an event to view details'}
                </p>
              </div>
              {selectedEvent ? <div className="px-4 py-5 sm:p-6" data-id="element-2551">
                  <div className="mb-6 flex items-center justify-between" data-id="element-2552">
                    <div className="flex items-center" data-id="element-2553">
                      {renderSeverityBadge(selectedEvent.severity)}
                      <h4 className="ml-2 text-lg font-medium text-gray-900 dark:text-white" data-id="element-2554">
                        {selectedEvent.eventType}
                      </h4>
                    </div>
                    {!selectedEvent.acknowledged && <button onClick={() => setAcknowledging(true)} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-[#2DB489] dark:bg-[#4ED1A0] hover:bg-[#25A077] dark:hover:bg-[#3BBE8E] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0]" data-id="element-2555">
                        <CheckCircleIcon className="h-4 w-4 mr-1" data-id="element-2556" />
                        Acknowledge
                      </button>}
                  </div>
                  <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2" data-id="element-2557">
                    <div data-id="element-2558">
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-2559">
                        Event ID
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-2560">
                        {selectedEvent.id}
                      </dd>
                    </div>
                    <div data-id="element-2561">
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-2562">
                        Timestamp
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-2563">
                        {formatDate(selectedEvent.timestamp)}
                      </dd>
                    </div>
                    <div data-id="element-2564">
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-2565">
                        Category
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-2566">
                        {renderCategoryBadge(selectedEvent.category)}
                      </dd>
                    </div>
                    <div data-id="element-2567">
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-2568">
                        Status
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-2569">
                        {selectedEvent.acknowledged ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300" data-id="element-2570">
                            <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-2571" />
                            Acknowledged by {selectedEvent.acknowledgedBy} on{' '}
                            {selectedEvent.acknowledgedAt ? formatDate(selectedEvent.acknowledgedAt) : 'unknown date'}
                          </span> : <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300" data-id="element-2572">
                            <AlertTriangleIcon className="w-3 h-3 mr-1" data-id="element-2573" />
                            Unacknowledged
                          </span>}
                      </dd>
                    </div>
                    {selectedEvent.userId && <div data-id="element-2574">
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-2575">
                          User ID
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-2576">
                          {selectedEvent.userId}
                        </dd>
                      </div>}
                    {selectedEvent.targetId && <div data-id="element-2577">
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-2578">
                          Target
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-2579">
                          {selectedEvent.targetType}: {selectedEvent.targetId}
                        </dd>
                      </div>}
                    {selectedEvent.ipAddress && <div data-id="element-2580">
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-2581">
                          IP Address
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-2582">
                          {selectedEvent.ipAddress}
                        </dd>
                      </div>}
                    {selectedEvent.location && <div data-id="element-2583">
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-2584">
                          Location
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-2585">
                          {selectedEvent.location}
                        </dd>
                      </div>}
                  </div>
                  <div className="mt-6" data-id="element-2586">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-2587">
                      Event Details
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-2588">
                      <div className="bg-gray-50 dark:bg-[#1A3330] p-4 rounded-md overflow-auto max-h-64" data-id="element-2589">
                        <pre className="text-xs whitespace-pre-wrap break-words" data-id="element-2590">
                          {JSON.stringify(selectedEvent.details, null, 2)}
                        </pre>
                      </div>
                    </dd>
                  </div>
                </div> : <div className="px-4 py-5 sm:p-6 flex flex-col items-center justify-center h-64" data-id="element-2591">
                  <TargetIcon className="h-12 w-12 text-gray-400 dark:text-gray-500" data-id="element-2592" />
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400" data-id="element-2593">
                    Select an event from the list to view its details
                  </p>
                </div>}
            </div>
          </div>
        </div>
      </div>
      {/* Acknowledge Modal */}
      {acknowledging && selectedEvent && <div className="fixed z-10 inset-0 overflow-y-auto" data-id="element-2594">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-2595">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" data-id="element-2596">
              <div className="absolute inset-0 bg-gray-500 dark:bg-gray-900 opacity-75" data-id="element-2597"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-2598">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white dark:bg-[#162825] rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-2599">
              <div className="bg-white dark:bg-[#162825] px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-2600">
                <div className="sm:flex sm:items-start" data-id="element-2601">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900 sm:mx-0 sm:h-10 sm:w-10" data-id="element-2602">
                    <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-300" data-id="element-2603" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left" data-id="element-2604">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="modal-title" data-id="element-2605">
                      Acknowledge Security Event
                    </h3>
                    <div className="mt-2" data-id="element-2606">
                      <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2607">
                        You are about to acknowledge the security event:{' '}
                        <strong data-id="element-2608">{selectedEvent.eventType}</strong>. This will
                        mark the event as reviewed and addressed.
                      </p>
                      <div className="mt-4" data-id="element-2609">
                        <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-2610">
                          Notes (optional)
                        </label>
                        <textarea id="notes" name="notes" rows={3} className="mt-1 block w-full rounded-md border-gray-300 dark:border-[#1A3330] shadow-sm focus:border-[#2DB489] dark:focus:border-[#4ED1A0] focus:ring focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:ring-opacity-50 dark:bg-[#1A3330] dark:text-white" placeholder="Add any notes or comments about how this event was addressed..." value={acknowledgeNotes} onChange={e => setAcknowledgeNotes(e.target.value)} data-id="element-2611"></textarea>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-[#1A3330] px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-2612">
                <button type="button" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-[#2DB489] dark:bg-[#4ED1A0] text-base font-medium text-white hover:bg-[#25A077] dark:hover:bg-[#3BBE8E] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] sm:ml-3 sm:w-auto sm:text-sm" onClick={handleAcknowledge} data-id="element-2613">
                  Acknowledge
                </button>
                <button type="button" className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-[#264D49] shadow-sm px-4 py-2 bg-white dark:bg-[#162825] text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-[#1A3330] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" onClick={() => {
              setAcknowledging(false);
              setAcknowledgeNotes('');
            }} data-id="element-2614">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
};